import {
  LayoutDashboard,
  Radio,
  ClipboardCheck,
  Users,
  LogOut,
  FileText,
  Building2,
  TrendingUp,
  BarChart3,
  Receipt,
  CreditCard,
  IdCard,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const CoreModulesSection = () => {
  const modules = [
    {
      icon: LayoutDashboard,
      title: 'Workplace Intelligence',
      description: 'Real-time insights on team dynamics, engagement trends, sentiment, and workforce patterns.',
      detailedContent: 'Real-time insights on team dynamics, engagement trends, sentiment, and workforce patterns. Detect shifts in morale, productivity, and collaboration before they affect performance. See which teams are thriving and which need support, presented as clear, visual dashboards. Turn insight into action with automated alerts, suggested interventions, and measurable follow-ups.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Radio,
      title: 'Company Broadcast',
      description: 'Keep everyone connected. Instant communication to teams or individuals across your organization.',
      detailedContent: 'Keep everyone connected with instant communication to teams or individuals. Send company-wide announcements, team updates, or 1-on-1 messages. Schedule broadcasts, track read receipts, and ensure important messages reach everyone. Create communication channels by department, location, or project for targeted messaging.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      icon: ClipboardCheck,
      title: 'Welcome Experience',
      description: 'Create memorable first days. Track pre-joining tasks and make new hires feel valued from day zero.',
      detailedContent: 'Create memorable first days for new joiners. Automate pre-joining tasks like document collection, IT setup requests, and welcome communications. Build custom onboarding journeys with checklists, introductions, and milestone celebrations. Track completion status and ensure nothing falls through the cracks during those critical first weeks.',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      icon: Users,
      title: 'People Directory',
      description: 'Complete employee profiles: personal details, work history, documents, assets; everything in one place.',
      detailedContent: 'Centralized people directory with complete employee profiles. Store personal information, employment history, compensation details, documents, IT assets, and uniform records. Quick search and filter capabilities. Role-based access ensures privacy while maintaining transparency. Export reports and integrate with existing systems seamlessly.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      icon: LogOut,
      title: 'Graceful Transitions',
      description: 'Structured offboarding with task tracking, knowledge transfer, and meaningful exit conversations.',
      detailedContent: 'Make departures as smooth as arrivals. Structured offboarding workflows with automated task assignments for IT, HR, and managers. Conduct meaningful exit interviews to gather honest feedback. Ensure knowledge transfer happens before departure. Track asset returns, access revocation, and final settlements with full audit trails.',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    {
      icon: FileText,
      title: 'Task Management',
      description: 'Assign and track work across departments. Keep projects moving and accountability clear.',
      detailedContent: 'Assign and track tasks across departments with clear ownership and deadlines. Create recurring checklists for standard processes. Set priorities, add dependencies, and receive notifications when tasks need attention. Visual dashboards show bottlenecks and completion rates. Integrate tasks with employee journeys for automated workflows.',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
    },
    {
      icon: Building2,
      title: 'Document Hub',
      description: 'Secure storage with AI-powered search. Share internally or externally with confidence.',
      detailedContent: 'Secure document repository with AI-powered search and organization. Upload, categorize, and share files with granular permissions. Version control ensures you always have the latest copy. Set expiration dates for time-sensitive documents. AI extracts key information and makes content searchable beyond just file names.',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
    },
    {
      icon: TrendingUp,
      title: 'Growth & Development',
      description: 'AI-driven performance insights, goal tracking, and continuous feedback loops.',
      detailedContent: 'Revolutionize performance management into growth conversations. Set and track goals with progress updates. Conduct 360-degree reviews with customizable templates. AI analyzes patterns and suggests development areas. Continuous feedback replaces annual reviews with regular check-ins. Link performance to compensation and career progression.',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
    },
    {
      icon: BarChart3,
      title: 'Engagement Pulse',
      description: 'Measure eNPS, sentiment, and team health. Identify engagement hotspots in real-time.',
      detailedContent: 'Measure team engagement with pulse surveys and eNPS tracking. Anonymous feedback encourages honest responses. AI identifies sentiment trends and engagement hotspots across teams and locations. Real-time dashboards show what\'s working and what needs attention. Benchmark against industry standards and track improvements over time.',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
    },
    {
      icon: Receipt,
      title: 'Expense Management',
      description: 'Smart expense forms with auto-fill, approval workflows, and real-time tracking.',
      detailedContent: 'Streamline expense claims with smart forms that auto-populate common entries. Configurable approval workflows route claims to the right approvers. Set spending limits by category and employee level. Attach receipts with mobile upload. Track reimbursement status in real-time. Export to accounting systems with one click.',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
    {
      icon: IdCard,
      title: 'Digital Identity',
      description: 'Dynamic employee IDs that auto-update with profile changes. Professional and always current.',
      detailedContent: 'Generate professional digital employee IDs that stay current automatically. Every profile update refreshes the ID card instantly. Include photos, QR codes for access control, and emergency contact details. Employees can download or share their IDs digitally. Customize card templates to match your brand identity.',
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
    },
    {
      icon: CreditCard,
      title: 'Smart Business Cards',
      description: 'Auto-generated digital cards with QR sharing. Professional networking made effortless.',
      detailedContent: 'Create professional digital business cards automatically from employee profiles. Share via QR code, WhatsApp, email, or download as PNG. Recipients scan to save contact details instantly. Cards update automatically when information changes. Choose from multiple design templates. HR issues and manages all cards from one dashboard.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      featured: true,
    },
  ];

  return (
    <section className="py-8 sm:py-10 md:py-12 bg-background">
      <div className="max-w-[745px] w-full mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 animate-fade-in">
          <Badge className="mb-3 bg-primary/10 text-primary border-primary/20 text-xs">
            Platform Capabilities
          </Badge>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            Everything Your Workplace Needs
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            From their first day to every day after, hirerkey<sup>™</sup> stays by your team's side—<span className="emphasize font-semibold text-foreground">guiding onboarding, simplifying daily tasks, and helping people grow</span> while your business runs smoother in the background.
          </p>
        </div>

        {/* Modules Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-x-3 sm:gap-x-4" style={{ rowGap: '15px' }}>
          {modules.map((module, index) => {
            const Icon = module.icon;
            return (
              <div
                key={index}
                className="group relative perspective-1000 animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s`, height: '16rem' }}
              >
                <div className="relative w-full h-full transition-transform duration-700 transform-style-3d group-hover:rotate-y-180">
                  {/* Front of Card */}
                  <Card
                    className="absolute inset-0 backface-hidden p-3 sm:p-4 pb-5 border-2 border-border/50 hover:border-primary/30 transition-all overflow-hidden flex flex-col"
                  >
                    {module.featured && (
                      <Badge className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[10px]">
                        Featured
                      </Badge>
                    )}
                    
                    {/* Top Section */}
                    <div className="flex-1">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${module.bgColor} flex items-center justify-center mb-2 sm:mb-3 transition-transform group-hover:scale-110`}>
                        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${module.color}`} />
                      </div>
                      <h3 className="font-bold mb-1.5" style={{ fontSize: '1rem' }}>{module.title}</h3>
                      <p className="text-muted-foreground leading-relaxed line-clamp-3" style={{ fontSize: '0.85rem' }}>
                        {module.description}
                      </p>
                    </div>
                    
                    {/* Bottom Section - Read More and Number */}
                    <div className="flex justify-between items-end mt-3">
                      <div className="inline-block px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium border border-primary/20">
                        Read More
                      </div>
                      <span className="font-bold" style={{ fontSize: '2rem', color: '#cfcfcf', lineHeight: '1' }}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </Card>

                  {/* Back of Card */}
                  <Card
                    className="absolute inset-0 backface-hidden rotate-y-180 p-3 sm:p-4 bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-white border-2 border-primary overflow-y-auto"
                  >
                    <div className="h-full flex flex-col">
                      <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                        </div>
                        <h3 className="text-xs sm:text-sm font-bold">{module.title}</h3>
                      </div>
                      <p className="text-[10px] sm:text-xs leading-relaxed text-white/90 flex-1">
                        {module.detailedContent}
                      </p>
                    </div>
                  </Card>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CoreModulesSection;
